import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import { Divider, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { DISTRIBUTOR, SUPER_ADMIN } from '../../../constants/users';
import { selectAdminType, selectIsDeptAdmin } from '../../../store/selectors/auth';
import { hasPermission } from '../../../utils/users';

interface IProps {
  anchorEl: HTMLElement | null;
  isDefault?: boolean;
  isActive?: boolean;
  onClose: () => void;
  onEdit: () => void;
  onSetAsDefault: () => void;
  onToggleHidden: (hidden: boolean) => void;
}

export const TemplateCardContextMenu: React.FC<IProps> = ({
  anchorEl,
  isDefault = false,
  isActive = false,
  onClose,
  onEdit,
  onSetAsDefault,
  onToggleHidden,
}) => {
  const adminType = useSelector(selectAdminType);
  const isDeptAdmin = useSelector(selectIsDeptAdmin);
  const canSetAsDefault = useMemo(() => hasPermission([SUPER_ADMIN, DISTRIBUTOR], adminType), [adminType]);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      onClose();
    },
    [onClose],
  );

  const handleSetAsDefaultClick = useCallback(() => {
    onSetAsDefault();
  }, [onSetAsDefault]);

  const handleToggleHiddenClick = useCallback(() => {
    onToggleHidden(isActive);
  }, [onToggleHidden, isActive]);

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      onContextMenu={handleContextMenu}
      autoFocus={false}
      disableAutoFocus={true}
      disableEnforceFocus={true}
      disableRestoreFocus={true}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      slotProps={{
        paper: {
          sx: {
            width: 275,
          },
        },
      }}
    >
      {!isDeptAdmin && (
        <MenuItem onClick={onEdit} data-action="context_menu_rename">
          <ListItemIcon>
            <EditOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
      )}
      {canSetAsDefault && (
        <>
          <MenuItem
            onClick={handleSetAsDefaultClick}
            disabled={isDefault || !isActive}
            data-action="context_menu_set_as_org_default"
          >
            <ListItemIcon>
              <StarBorderIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Set as org default</ListItemText>
          </MenuItem>
          <Divider />
        </>
      )}
      <MenuItem
        onClick={handleToggleHiddenClick}
        disabled={isDefault}
        sx={{ alignItems: 'flex-start', py: 1.5 }}
        data-action={isActive ? 'context_menu_hide' : 'context_menu_make_live'}
      >
        <ListItemIcon sx={{ minWidth: 36, mt: 0.25 }}>
          {isActive ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
        </ListItemIcon>
        <ListItemText
          primary={isActive ? 'Hide' : 'Make live'}
          secondary={isActive ? 'Removes from selection for new sends' : 'Shows in selection for new sends'}
          slotProps={{
            primary: { sx: { fontSize: 16 } },
            secondary: { sx: { fontSize: 12 } },
          }}
        />
      </MenuItem>
    </Menu>
  );
};
